import { useState } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { Instagram, Globe } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const MASCOT = "https://customer-assets-jt897jd0.emergentagent.net/job_smoke-beach-drop/artifacts/g2ejgl6q_maskotka-obrys.webp";
const PRINT_FULL = "https://customer-assets-jt897jd0.emergentagent.net/job_smoke-beach-drop/artifacts/5tm5ju00_back-tlo.webp";

export const Footer = ({ t, lang }) => {
  const [email, setEmail] = useState("");
  const [sending, setSending] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email.trim())) {
      toast.error(t.modal.invalid);
      return;
    }
    setSending(true);
    try {
      const { data } = await axios.post(`${API}/drop-signup`, { email: email.trim(), lang });
      toast.success(data.duplicate ? t.modal.duplicate : t.modal.success);
      if (!data.duplicate) setEmail("");
    } catch {
      toast.error(t.modal.error);
    } finally {
      setSending(false);
    }
  };

  return (
    <footer data-testid="footer" className="relative overflow-hidden bg-[#0a0a0a]">
      <div className="halftone-light mx-auto max-w-7xl px-4 pt-20 sm:px-6 lg:px-8">
        <div className="grid gap-12 pb-16 lg:grid-cols-2">
          <div>
            <p className="font-script text-3xl text-[#e5b83b]">{t.footer.tagline}</p>
            <p className="mt-4 max-w-sm text-sm text-[#9a9890]">{t.footer.newsletter}</p>
            <form onSubmit={submit} className="mt-6 flex max-w-md flex-wrap gap-3">
              <input
                data-testid="footer-email-input"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder={t.footer.placeholder}
                className="w-full min-w-0 flex-1 rounded-xl border-[3px] border-[#0a0a0a] bg-[#f4f1ea] px-4 py-3 font-mono2 text-sm text-[#0a0a0a] shadow-[4px_4px_0_#d96b43] outline-none placeholder:text-[#0a0a0a]/40"
              />
              <button
                data-testid="footer-submit-button"
                type="submit"
                disabled={sending}
                className="btn-toon shrink-0 bg-[#d96b43] px-7 py-3 text-lg text-[#0a0a0a] disabled:opacity-50"
              >
                {sending ? "…" : t.footer.submit}
              </button>
            </form>
          </div>
          <div className="flex flex-col justify-between gap-8 lg:items-end">
            <div className="flex gap-4">
              <a data-testid="footer-instagram" href="https://www.instagram.com/signupp.co" target="_blank" rel="noreferrer" className="btn-toon flex h-12 w-12 items-center justify-center bg-[#f4f1ea] text-[#0a0a0a]">
                <Instagram size={19} />
              </a>
              <a data-testid="footer-website" href="https://signupp.co" target="_blank" rel="noreferrer" className="btn-toon flex h-12 w-12 items-center justify-center bg-[#f4f1ea] text-[#0a0a0a]">
                <Globe size={19} />
              </a>
            </div>
            <img src={MASCOT} alt="$IGN UP! mascot" className="w-36 object-contain" />
          </div>
        </div>
      </div>

      <div className="relative">
        <img src={PRINT_FULL} alt="" aria-hidden className="pointer-events-none absolute inset-x-0 bottom-0 mx-auto w-full max-w-4xl opacity-25 mix-blend-screen" />
        <motion.h2
          data-testid="footer-bigtype"
          className="toon-text pointer-events-none relative select-none whitespace-nowrap text-center font-script text-[17vw] leading-[0.9] text-[#f4f1ea]/95"
          initial={{ y: 90, opacity: 0 }}
          whileInView={{ y: 0, opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.9, ease: [0.34, 1.56, 0.64, 1] }}
        >
          $ign up!
        </motion.h2>
      </div>

      <div className="border-t-[3px] border-[#1a1a1e] py-5">
        <p data-testid="footer-rights" className="text-center font-mono2 text-[10px] font-bold tracking-[0.3em] text-[#9a9890]">{t.footer.rights}</p>
      </div>
    </footer>
  );
};
